import { useEffect, useState } from 'react';

import { showSnackbar } from 'fluxo-ui';
import { PlayIcon } from 'fluxo-ui/icons';

import { LiveQuizSettings } from '../../types/index.ts';
import { EXAM_TYPES } from '../common/constants';
import { LiveQuizHost, LiveQuizSetup } from '../components/livequiz';
import { useExamStore } from '../store/examStore';
import { useLiveQuizStore } from '../store/liveQuizStore';

const LiveQuizPage: React.FC = () => {
    const { exams, fetchExams } = useExamStore();
    const { session, loading, createSession, disconnect } = useLiveQuizStore();

    const [selectedExamId, setSelectedExamId] = useState('');

    useEffect(() => {
        fetchExams({ status: 'active', type: EXAM_TYPES.LIVE_QUIZ });
    }, [fetchExams]);

    useEffect(() => () => disconnect(), [disconnect]);

    const handleCreateSession = async (settings: LiveQuizSettings) => {
        const examId = selectedExamId || exams[0]?._id;
        if (!examId) {
            showSnackbar('Select a quiz to host first', 'No quiz selected', { type: 'error' });
            return;
        }
        try {
            await createSession(examId, settings);
        } catch (error) {
            showSnackbar(error instanceof Error ? error.message : 'Could not create the session', 'Failed', {
                type: 'error',
            });
        }
    };

    if (session) {
        return <LiveQuizHost session={session} />;
    }

    const selectedExam = exams.find((exam) => exam._id === selectedExamId) ?? exams[0];

    return (
        <div className="max-w-2xl mx-auto space-y-6">
            <div>
                <h1 className="text-xl font-semibold text-slate-900 dark:text-white">Host a live quiz</h1>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    Players join with a code and race for the top of the leaderboard.
                </p>
            </div>

            <section className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
                <h2 className="mb-4 text-sm font-semibold text-slate-900 dark:text-white">Choose a quiz</h2>

                {exams.length === 0 ? (
                    <div className="py-8 text-center">
                        <PlayIcon className="mx-auto w-8 h-8 text-slate-300 dark:text-slate-700" />
                        <p className="mt-3 text-sm font-medium text-slate-900 dark:text-white">No live quizzes yet</p>
                        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                            Create an exam of type "Live Quiz" first, then come back to host it.
                        </p>
                    </div>
                ) : (
                    <ul className="space-y-2">
                        {exams.map((exam) => {
                            const isSelected = (selectedExamId || exams[0]?._id) === exam._id;

                            return (
                                <li key={exam._id}>
                                    <button
                                        type="button"
                                        onClick={() => setSelectedExamId(exam._id)}
                                        className={
                                            isSelected
                                                ? 'cursor-pointer w-full text-left rounded-md border-2 border-teal-500 bg-teal-50 dark:bg-teal-950/40 px-4 py-3 transition-colors'
                                                : 'cursor-pointer w-full text-left rounded-md border border-slate-200 dark:border-slate-800 px-4 py-3 hover:border-slate-300 dark:hover:border-slate-700 transition-colors'
                                        }
                                    >
                                        <span className="block text-sm font-medium text-slate-900 dark:text-white">{exam.title}</span>
                                        <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">
                                            {exam.questions?.length ?? 0} questions
                                        </span>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </section>

            {selectedExam && (
                <LiveQuizSetup
                    examTitle={selectedExam.title}
                    questionCount={selectedExam.questions?.length ?? 0}
                    starting={loading}
                    onStart={handleCreateSession}
                />
            )}
        </div>
    );
};

export default LiveQuizPage;
